import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  Users2,
  FileText,
  Megaphone,
  GitBranch,
  BarChart3,
  BookOpen,
  Bot,
  Headphones,
  ShoppingBag,
  Puzzle,
  Settings,
  Kanban,
  ListChecks,
  Images,
  ArrowUpDown,
  Wallet,
  BookText,
  SmilePlus,
  Activity,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useThemeStore } from '@/stores/themeStore'
import { useAuth } from '@/hooks/useAuth'
import { useSidebar } from '@/hooks/useSidebar'
import { useWaba } from '@/hooks/useWaba'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { inboxApi } from '@/api/endpoints/inbox'
import { codenamesForPath } from '@/lib/sidebar-permissions'
import { resolveSidebarIcon } from '@/components/layout/sidebar-icons'
import { BrandLogo } from '@/components/shared/BrandLogo'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface NavItem {
  to: string
  icon: LucideIcon
  label: string
  badge?: 'dot' | 'new'
  /** Permission codename required to see this nav item. Omit for unrestricted. */
  permission?: string
  /** Any one of these permissions grants access (used when both import & export should see the page). */
  permissions?: string[]
  /** Feature flag key required to see this nav item (AND permission check). */
  feature?: string
}

interface NavGroup {
  label: string
  items: NavItem[]
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/inbox', icon: MessageSquare, label: 'Inbox', badge: 'dot', permission: 'view_inbox' },
      { to: '/customers', icon: Users, label: 'Customers', permission: 'view_customers' },
      { to: '/segments', icon: Users2, label: 'Segments', permission: 'view_customers' },
    ],
  },
  {
    label: 'Engage',
    items: [
      { to: '/templates', icon: FileText, label: 'Templates', permission: 'view_templates' },
      { to: '/campaigns', icon: Megaphone, label: 'Campaigns', permission: 'view_campaigns' },
      { to: '/activity-log', icon: Activity, label: 'API Activity' },
      { to: '/flows', icon: GitBranch, label: 'Flow Builder', permission: 'view_flows' },
      { to: '/wa-flows', icon: ListChecks, label: 'WhatsApp Flows', permission: 'view_whatsapp_flows' },
      { to: '/setu-media', icon: Images, label: 'Setu Media', permission: 'view_media_library' },
    ],
  },
  {
    label: 'Operate',
    items: [
      { to: '/pipeline', icon: Kanban, label: 'Pipeline', permission: 'view_pipeline' },
      { to: '/ai-chatbot', icon: Bot, label: 'AI Chatbot', permission: 'view_chatbot' },
      { to: '/knowledge-base', icon: BookOpen, label: 'Knowledge Base', permission: 'view_knowledge_base' },
      { to: '/analytics', icon: BarChart3, label: 'Analytics', permission: 'view_analytics' },
      { to: '/import-export', icon: ArrowUpDown, label: 'Import/Export', permissions: ['import_customers', 'export_customers'] },
    ],
  },
  {
    label: 'Connect',
    items: [
      { to: '/ecommerce', icon: ShoppingBag, label: 'E-commerce', permission: 'view_ecommerce' },
      { to: '/csat', icon: SmilePlus, label: 'CSAT', permission: 'view_csat' },
      { to: '/integrations', icon: Puzzle, label: 'Integrations', permission: 'view_integrations' },
      { to: '/billing', icon: Wallet, label: 'Billing', permission: 'view_billing' },
      { to: '/settings', icon: Settings, label: 'Settings' },
      { to: '/documents', icon: BookText, label: 'Documents', permission: 'view_documents' },
    ],
  },
]

/**
 * Is a nav item's feature flag satisfied?
 *
 * Only consulted for the STATIC fallback groups — the backend sidebar
 * (`/api/auth/sidebar/`) already applies `SidebarItem.feature_flag` against the
 * company's `CompanyFeatureFlag` rows, so its items arrive pre-filtered.
 *
 * Same policy as `FeatureGate`: decide only on a definitive payload. While the
 * flags are still loading (or the endpoint failed) we keep the item visible
 * rather than flickering it away — the route's own `FeatureGate` is the
 * authoritative check and will still bounce a company that lacks the feature.
 */
function isFeatureSatisfied(item: NavItem, enabledFeatures: string[] | undefined): boolean {
  if (!item.feature) return true
  if (!enabledFeatures) return true
  return enabledFeatures.includes(item.feature)
}

function canSeeNavItem(
  item: NavItem,
  hasCodename: (codenames: string | string[], requireAll?: boolean) => boolean,
  enabledFeatures?: string[],
): boolean {
  if (!isFeatureSatisfied(item, enabledFeatures)) return false
  if (item.permissions?.length) {
    return hasCodename(item.permissions)
  }
  if (item.permission) {
    return hasCodename(item.permission)
  }
  const routeCodenames = codenamesForPath(item.to)
  if (!routeCodenames.length) return true
  return hasCodename(routeCodenames)
}

function filterNavGroups(
  groups: NavGroup[],
  hasCodename: (codenames: string | string[], requireAll?: boolean) => boolean,
  enabledFeatures?: string[],
): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => canSeeNavItem(item, hasCodename, enabledFeatures)),
    }))
    .filter((group) => group.items.length > 0)
}

/**
 * Live count of contacts with unread messages for the Inbox nav item.
 * Replaces the old always-on green dot: shows a number badge only when
 * customers are actually waiting (e.g. 2 customers messaged → "2").
 * WebSocket message events invalidate the query; polling is the fallback.
 */
function useInboxUnreadCount(): number {
  const { selectedWabaId } = useWaba()
  const { data } = useQuery({
    queryKey: ['inbox-unread-count', selectedWabaId],
    queryFn: () => inboxApi.getUnreadCount(selectedWabaId!),
    enabled: !!selectedWabaId,
    refetchInterval: 30_000,
    staleTime: 15_000,
  })
  return data ?? 0
}

function InboxUnreadBadge({ collapsed }: { collapsed?: boolean }) {
  const count = useInboxUnreadCount()
  if (count <= 0) return null
  const label = count > 99 ? '99+' : String(count)
  if (collapsed) {
    return (
      <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[9px] font-semibold text-white ring-2 ring-white dark:ring-clay-800">
        {label}
      </span>
    )
  }
  return (
    <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-error px-1.5 text-[10px] font-semibold text-white">
      {label}
    </span>
  )
}

/* Sidebar geometry — 240px open, 80px collapsed, 48px rows, 64px logo block. */
export const SIDE_NAV_WIDTH = 230
export const SIDE_NAV_COLLAPSED_WIDTH = 80

/* ── Expanded row: a bulleted child of a dropdown group ─── */
function ExpandedNavRow({
  item,
  onMobileClose,
}: {
  item: NavItem
  onMobileClose?: () => void
}) {
  const { to, label, badge } = item

  return (
    <NavLink
      to={to}
      onClick={onMobileClose}
      className={({ isActive }) =>
        cn(
          'group/row flex h-10 w-full items-center gap-x-3 rounded-lg px-3 text-sm transition-colors duration-150',
          isActive
            ? 'menu-item-active font-semibold text-primary'
            : 'font-medium text-clay-500 hover:bg-clay-50 hover:text-clay-900 dark:text-clay-400 dark:hover:bg-clay-700/50 dark:hover:text-clay-100',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* 20px slot = the group icon's width, so the label lines up with
              the group label above it. */}
          <span className="flex w-5 shrink-0 justify-center">
            <span
              className={cn(
                'rounded-full transition-all duration-150',
                isActive
                  ? 'h-1.5 w-1.5 bg-primary'
                  : 'h-1.5 w-1.5 bg-clay-300 group-hover/row:bg-clay-500 dark:bg-clay-600',
              )}
            />
          </span>
          <span className="flex-1 truncate">{label}</span>

          {badge === 'dot' &&
            (to === '/inbox' ? (
              <InboxUnreadBadge />
            ) : (
              <span className="h-2 w-2 shrink-0 rounded-full bg-error" />
            ))}

          {badge === 'new' && (
            <span className="rounded-md bg-primary-subtle px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
              New
            </span>
          )}
        </>
      )}
    </NavLink>
  )
}

/* ── Expanded group: a dropdown row, like the Unnati ERP sidebar ─ */

/** Icon shown on each group's dropdown row. Keys are lower-cased labels. */
const GROUP_ICONS: Record<string, LucideIcon> = {
  overview: LayoutDashboard,
  engage: Megaphone,
  operate: Headphones,
  connect: Puzzle,
}

/** Legacy key from when several groups could stay open at once. The sidebar is
 *  an accordion now (open group derives from the route, like Unnati), so the
 *  stored list is cleared rather than restored. */
const LEGACY_OPEN_GROUPS_KEY = 'setu.sidebar.openGroups'

function isRouteActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`)
}

function groupIconFor(group: NavGroup): LucideIcon {
  // Backend-driven groups may use labels we have no icon for; borrow the
  // first item's icon so the row never renders blank.
  return GROUP_ICONS[group.label.toLowerCase()] ?? group.items[0]?.icon ?? LayoutDashboard
}

function groupSlug(label: string): string {
  return label.toLowerCase().replace(/\W+/g, '-')
}

/* ── Collapsed: one icon per group + a hover flyout ───────
   Mirrors the Unnati ERP mini sidebar: the rail carries a single icon per
   section and hovering it opens a white rounded card to the right listing
   that section's pages. Built on Radix DropdownMenu so the panel is
   portalled (never clipped by the ScrollArea) and Escape / arrow keys /
   Enter-Space come for free; hover is layered on top with open/close delays
   so the pointer can travel across the gap into the panel. */

const FLYOUT_OPEN_DELAY = 80
const FLYOUT_CLOSE_DELAY = 180

/** Row inside the flyout — the group's page, with its own icon + badge. */
function FlyoutNavRow({
  item,
  active,
  onNavigate,
}: {
  item: NavItem
  active: boolean
  onNavigate?: () => void
}) {
  const { to, icon: Icon, label, badge } = item

  return (
    <DropdownMenuItem asChild>
      {/* NavLink gets a plain string className: `asChild` merges className as
          a string, so a function-className would be stringified and every
          class silently dropped. Active state is computed from the router
          location instead. */}
      <NavLink
        to={to}
        onClick={onNavigate}
        className={cn(
          'flex w-full items-center gap-x-3',
          active && 'text-primary focus:text-primary dark:text-primary dark:focus:text-primary',
        )}
      >
        <Icon className="h-[1.125rem] w-[1.125rem] shrink-0" strokeWidth={1.75} />
        <span className="flex-1 truncate">{label}</span>

        {badge === 'dot' &&
          (to === '/inbox' ? (
            <InboxUnreadBadge />
          ) : (
            <span className="h-2 w-2 shrink-0 rounded-full bg-error" />
          ))}

        {badge === 'new' && (
          <span className="rounded-md bg-primary-subtle px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
            New
          </span>
        )}
      </NavLink>
    </DropdownMenuItem>
  )
}

function CollapsedNavGroup({
  group,
  onMobileClose,
}: {
  group: NavGroup
  onMobileClose?: () => void
}) {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }, [])

  useEffect(() => clearTimer, [clearTimer])

  const scheduleOpen = useCallback(() => {
    clearTimer()
    timer.current = setTimeout(() => setOpen(true), FLYOUT_OPEN_DELAY)
  }, [clearTimer])

  const scheduleClose = useCallback(() => {
    clearTimer()
    timer.current = setTimeout(() => setOpen(false), FLYOUT_CLOSE_DELAY)
  }, [clearTimer])

  const containsActive = group.items.some((item) => isRouteActive(pathname, item.to))
  const GroupIcon = groupIconFor(group)

  const unread = useInboxUnreadCount()
  const hasInboxBadge = group.items.some((item) => item.to === '/inbox' && item.badge === 'dot')
  const hasOtherBadge = group.items.some(
    (item) => item.badge === 'new' || (item.badge === 'dot' && item.to !== '/inbox'),
  )
  const showGroupDot = hasOtherBadge || (hasInboxBadge && unread > 0)

  const close = () => {
    clearTimer()
    setOpen(false)
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        clearTimer()
        setOpen(next)
      }}
      modal={false}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={group.label}
          data-testid={`collapsed-nav-group-${groupSlug(group.label)}`}
          onPointerEnter={(e) => {
            if (e.pointerType === 'mouse') scheduleOpen()
          }}
          onPointerLeave={(e) => {
            if (e.pointerType === 'mouse') scheduleClose()
          }}
          className={cn(
            'menu-item menu-item-hoverable mb-1 h-12 justify-center px-0',
            (containsActive || open) &&
              'bg-primary-subtle hover:bg-primary-subtle dark:hover:bg-primary-subtle',
            containsActive && 'menu-item-active',
          )}
        >
          <span className="relative flex">
            <GroupIcon className="h-6 w-6" strokeWidth={1.75} />
            {showGroupDot && (
              <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-error ring-2 ring-white dark:ring-clay-800" />
            )}
          </span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="right"
        align="start"
        sideOffset={10}
        alignOffset={-4}
        className="z-[60] min-w-[14rem] max-w-[18rem]"
        onPointerEnter={() => clearTimer()}
        onPointerLeave={(e) => {
          if (e.pointerType === 'mouse') scheduleClose()
        }}
      >
        <p className="px-3 pb-1.5 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-clay-400 dark:text-clay-500">
          {group.label}
        </p>
        {group.items.map((item) => (
          <FlyoutNavRow
            key={item.to}
            item={item}
            active={isRouteActive(pathname, item.to)}
            onNavigate={() => {
              close()
              onMobileClose?.()
            }}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ExpandedNavGroup({
  group,
  open,
  onToggle,
  onMobileClose,
}: {
  group: NavGroup
  open: boolean
  onToggle: () => void
  onMobileClose?: () => void
}) {
  const { pathname } = useLocation()
  const containsActive = group.items.some((item) => isRouteActive(pathname, item.to))
  const GroupIcon = groupIconFor(group)
  const panelId = `sidebar-group-${groupSlug(group.label)}`

  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(
          'flex h-11 w-full items-center gap-x-3 rounded-xl px-3 text-sm font-semibold transition-colors duration-150',
          // The section holding the current page is tinted in the primary
          // colour, open or closed, so parent and child read as one selection.
          containsActive
            ? 'bg-primary-subtle text-primary hover:bg-primary-subtle dark:bg-primary-subtle dark:text-primary'
            : open
              ? 'bg-clay-100 text-clay-900 dark:bg-clay-700 dark:text-clay-50'
              : 'text-clay-700 hover:bg-clay-50 hover:text-clay-900 dark:text-clay-300 dark:hover:bg-clay-700/50 dark:hover:text-clay-50',
        )}
      >
        <GroupIcon className="h-5 w-5 shrink-0" strokeWidth={1.75} />
        <span className="flex-1 truncate text-left">{group.label}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 transition-transform duration-200',
            open && 'rotate-180',
            containsActive
              ? 'text-primary'
              : open
                ? 'text-clay-600 dark:text-clay-300'
                : 'text-clay-400',
          )}
          strokeWidth={2}
        />
      </button>

      {/* grid-rows 0fr→1fr animates to the content's natural height. The
          links stay mounted so search, tests and screen readers can still
          find them; `inert` keeps a closed group out of the tab order. */}
      <div
        id={panelId}
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-in-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
        {...(!open && { inert: '' })}
        aria-hidden={!open}
      >
        <div className="overflow-hidden">
          <div className="space-y-0.5 pb-2 pt-1.5">
            {group.items.map((item) => (
              <ExpandedNavRow key={item.to} item={item} onMobileClose={onMobileClose} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── Drawer breakpoint ─────────────────────────────────────
   Below Tailwind's `lg` (1024px) the sidebar is an off-canvas drawer. A
   drawer is always shown expanded (labels visible) regardless of the
   desktop collapse preference — a hover-flyout icon rail makes no sense on
   touch screens. Guarded for jsdom / SSR where matchMedia is missing. */
const DRAWER_QUERY = '(max-width: 1023.98px)'

function useIsDrawerViewport(): boolean {
  const get = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(DRAWER_QUERY).matches
      : false
  const [isDrawer, setIsDrawer] = useState(get)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(DRAWER_QUERY)
    const onChange = () => setIsDrawer(mql.matches)
    onChange()
    mql.addEventListener?.('change', onChange)
    return () => mql.removeEventListener?.('change', onChange)
  }, [])
  return isDrawer
}

/* ── Main Sidebar ────────────────────────────────────────── */
interface SidebarProps {
  mobileOpen?: boolean
  onMobileClose?: () => void
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const { sidebarCollapsed } = useThemeStore()
  const { hasCodename } = useAuth()
  const isDrawer = useIsDrawerViewport()
  const isCollapsed = sidebarCollapsed && !isDrawer

  const { data: dynamicSidebar } = useSidebar()
  // Only the static fallback needs these: backend-driven items are already
  // feature-filtered server-side.
  const { data: featureFlags } = useFeatureFlags()
  const enabledFeatures = featureFlags?.enabled_features

  const staticNavGroups = useMemo(
    () => filterNavGroups(NAV_GROUPS, hasCodename, enabledFeatures),
    [hasCodename, enabledFeatures],
  )

  const navGroups = useMemo<NavGroup[]>(() => {
    if (dynamicSidebar && dynamicSidebar.length > 0) {
      const dynamicGroups = dynamicSidebar
        .map((section) => ({
          label: section.label,
          items: section.items
            .filter((item) => !item.is_tabbing)
            .map((item) => ({
              to: item.to,
              label: item.label,
              icon: resolveSidebarIcon(item.icon),
              badge: item.badge ?? undefined,
            })),
        }))
        .filter((group) => group.items.length > 0)

      return filterNavGroups(dynamicGroups, hasCodename)
    }
    return staticNavGroups
  }, [dynamicSidebar, staticNavGroups, hasCodename])

  const width = isCollapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH

  // Accordion, like the Unnati ERP sidebar: at most one dropdown group is open.
  // Opening a group closes the others, and navigating opens the group holding
  // the current page (closing the rest) so the active link is always visible.
  const { pathname } = useLocation()
  const activeGroupLabel = useMemo(
    () =>
      navGroups.find((group) => group.items.some((item) => isRouteActive(pathname, item.to)))
        ?.label ?? null,
    [navGroups, pathname],
  )
  const [openGroup, setOpenGroup] = useState<string | null>(activeGroupLabel)

  useEffect(() => {
    if (activeGroupLabel) setOpenGroup(activeGroupLabel)
    // Re-run on every navigation, not only when the active group changes, so a
    // manually collapsed group re-opens when one of its pages is visited.
  }, [pathname, activeGroupLabel])

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_OPEN_GROUPS_KEY)
    } catch {
      // storage can be unavailable (private mode); nothing to clean up
    }
  }, [])

  const toggleGroup = (label: string) =>
    setOpenGroup((current) => (current === label ? null : label))

  return (
    <>
      <aside
        style={{ width, minWidth: width }}
        className={cn(
          'sidebar-surface fixed inset-y-0 left-0 z-50 flex h-screen shrink-0 flex-col supports-[height:100dvh]:h-dvh',
          'transition-all duration-200 ease-in-out print:hidden',
          'lg:sticky lg:top-0 lg:z-20',
          mobileOpen ? 'translate-x-0 shadow-2xl lg:shadow-none' : '-translate-x-full lg:translate-x-0',
        )}
        aria-label="Main navigation"
      >
        {/* ── Brand — 64px block ───────────────────────────── */}
        <NavLink
          to="/dashboard"
          onClick={onMobileClose}
          className={cn(
            'flex h-16 shrink-0 items-center gap-3',
            isCollapsed ? 'justify-center px-2' : 'px-6',
          )}
        >
          {/* Collapsed: the bare mark at Unnati's visible mark size (~30px). */}
          <BrandLogo variant={isCollapsed ? 'mark' : 'full'} size={isCollapsed ? 30 : 40} />
        </NavLink>

        {/* ── Navigation ───────────────────────────────────── */}
        <ScrollArea className="flex-1">
          {isCollapsed ? (
            <nav className="px-4 pb-4 pt-1.5">
              {navGroups.map((group) => (
                <CollapsedNavGroup
                  key={group.label}
                  group={group}
                  onMobileClose={onMobileClose}
                />
              ))}
            </nav>
          ) : (
            <nav className="px-3 pb-4 pt-2">
              {navGroups.map((group) => (
                <ExpandedNavGroup
                  key={group.label}
                  group={group}
                  open={openGroup === group.label}
                  onToggle={() => toggleGroup(group.label)}
                  onMobileClose={onMobileClose}
                />
              ))}
            </nav>
          )}
        </ScrollArea>
      </aside>
    </>
  )
}
